/**
 * ClaimDestinyModal - Authentication Modal
 * 
 * Supports: Google Sign-In, Email/Password, and Guest Mode
 * Guest accounts can be upgraded later when user wants to save data permanently
 */
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowLeft, Loader2, Mail, Eye, EyeOff } from 'lucide-react';
import { useFirebaseAuth } from '@/hooks/useFirebaseAuth';
import { LegalLinks } from '@/components/LegalPages';
import { useLanguage } from '@/contexts/LanguageContext';

// Google brand icon (SVG)
const GoogleIcon = () => (
    <svg viewBox="0 0 24 24" className="w-5 h-5">
        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
);

const ClaimDestinyModal = ({ 
    isOpen, 
    onClose, 
    onSkipAsGuest,
    onSuccess, 
    guestData,
    isReturningUser = false
}) => {
    const {
        isLoading,
        error,
        signInWithGoogle,
        signUpWithEmail,
        signInWithEmail,
        resetPassword,
        signInAsGuest,
        setError
    } = useFirebaseAuth();
    
    const { language } = useLanguage();

    const [authMode, setAuthMode] = useState(null); // null | 'email' | 'signup' | 'forgot'
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [resetSent, setResetSent] = useState(false);

    // Reset state when modal opens
    useEffect(() => {
        if (isOpen) {
            const timer = setTimeout(() => {
                setAuthMode(null);
                setEmail('');
                setPassword('');
                setShowPassword(false);
                setResetSent(false);
                setError(null);
            }, 0);
            return () => clearTimeout(timer);
        }
    }, [isOpen, setError]);

    // Handle Google sign-in
    const handleGoogleSignIn = async () => {
        console.log('🔵 handleGoogleSignIn called - starting Google auth');
        try {
            const userData = await signInWithGoogle(guestData);
            // Note: For redirect flow, userData will be null as the browser navigates away
            // The actual result comes from getRedirectResult() when the page reloads
            if (userData) {
                onSuccess?.(userData);
                onClose?.();
            }
        } catch (err) {
            console.error('Google sign-in failed:', err);
        }
    };

    // Handle Guest sign-in (anonymous)
    const handleGuestSignIn = async () => {
        try {
            const userData = await signInAsGuest();
            if (userData) {
                // IMPORTANT: Call onSkipAsGuest for explicit guest mode choice
                // This triggers the proper guest state handling in OnboardingFlow
                onSkipAsGuest?.();
            }
        } catch (err) {
            console.error('Guest sign-in failed:', err);
        }
    };

    // Handle Email sign-in
    const handleEmailSignIn = async (e) => {
        e.preventDefault();
        if (!email || !password) {
            setError('Please enter email and password');
            return;
        }
        
        try {
            const userData = await signInWithEmail(email, password);
            if (userData) {
                onSuccess?.(userData);
                onClose?.();
            }
        } catch (err) {
            console.error('Email sign-in failed:', err);
        }
    };

    // Handle Email sign-up
    const handleEmailSignUp = async (e) => {
        e.preventDefault();
        if (!email || !password) {
            setError('Please enter email and password');
            return;
        }
        if (password.length < 6) {
            setError('Password must be at least 6 characters');
            return;
        }
        
        try {
            const userData = await signUpWithEmail(email, password, guestData);
            if (userData) {
                onSuccess?.(userData);
                onClose?.();
            }
        } catch (err) {
            console.error('Email sign-up failed:', err);
        }
    };

    // Handle password reset
    const handleResetPassword = async (e) => {
        e.preventDefault();
        if (!email) {
            setError('Please enter your email address');
            return;
        }
        
        try {
            await resetPassword(email);
            setResetSent(true);
        } catch (err) {
            console.error('Password reset failed:', err);
        }
    };

    // Go back
    const handleBack = () => {
        setAuthMode(null);
        setError(null);
        setResetSent(false);
    };

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <motion.div
                className="fixed inset-0 z-50 flex items-center justify-center p-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
            >
                {/* Backdrop */}
                <motion.div 
                    className="absolute inset-0 bg-black/80 backdrop-blur-md"
                    onClick={onClose}
                />

                {/* Modal */}
                <motion.div
                    className="relative z-10 w-full max-w-md overflow-hidden rounded-2xl"
                    style={{
                        background: 'linear-gradient(180deg, rgba(15, 15, 17, 0.98) 0%, rgba(8, 8, 10, 0.98) 100%)',
                        border: '1px solid rgba(212, 175, 55, 0.2)',
                        boxShadow: '0 0 60px rgba(212, 175, 55, 0.1)'
                    }}
                    initial={{ scale: 0.9, y: 30, opacity: 0 }}
                    animate={{ scale: 1, y: 0, opacity: 1 }}
                    exit={{ scale: 0.9, y: 30, opacity: 0 }}
                >
                    {/* Close Button */}
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 p-2 rounded-full transition-colors z-20"
                        style={{ background: 'rgba(212, 175, 55, 0.1)', color: 'rgba(212, 175, 55, 0.6)' }}
                        data-testid="close-modal-btn"
                    >
                        <X className="w-4 h-4" />
                    </button>

                    {/* Back Button */}
                    {authMode && (
                        <button
                            onClick={handleBack}
                            className="absolute top-4 left-4 p-2 rounded-full transition-colors z-20"
                            style={{ background: 'rgba(212, 175, 55, 0.1)', color: 'rgba(212, 175, 55, 0.6)' }}
                        >
                            <ArrowLeft className="w-4 h-4" />
                        </button>
                    )}

                    {/* Decorative glow */}
                    <div 
                        className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-32 pointer-events-none"
                        style={{ background: 'radial-gradient(ellipse at center, rgba(212, 175, 55, 0.15) 0%, transparent 70%)' }}
                    />

                    <div className="relative p-8 pt-12">
                        {/* Main Auth Selection */}
                        {!authMode && (
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                            >
                                {/* Cosmic Symbol */}
                                <div className="flex justify-center mb-6">
                                    <div 
                                        className="w-20 h-20 rounded-full flex items-center justify-center"
                                        style={{
                                            background: 'radial-gradient(circle, rgba(212, 175, 55, 0.2) 0%, transparent 70%)',
                                            border: '2px solid rgba(212, 175, 55, 0.3)'
                                        }}
                                    >
                                        <span className="text-4xl">✨</span>
                                    </div>
                                </div>

                                {/* Headline */}
                                <h2 
                                    className="font-cinzel text-2xl text-center mb-2"
                                    style={{
                                        background: 'linear-gradient(135deg, #F5D76E 0%, #D4AF37 50%, #AA8C2C 100%)',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                    }}
                                >
                                    {isReturningUser ? 'Welcome Back' : 'Enter the Sanctuary'}
                                </h2>

                                <p className="text-center text-primary/60 font-inter text-sm mb-8">
                                    {isReturningUser ? (
                                        <>Sign in to access your<br /><span className="text-primary/80 font-medium">cosmic journey</span>.</>
                                    ) : (
                                        <>Begin your cosmic journey<br />or <span className="text-primary/80 font-medium">try as guest</span> first.</>
                                    )}
                                </p>

                                {/* Auth Buttons */}
                                <div className="space-y-3">
                                    {/* Google Button (Primary) */}
                                    <motion.button
                                        onClick={handleGoogleSignIn}
                                        disabled={isLoading}
                                        className="w-full py-4 px-6 rounded-xl flex items-center justify-center gap-3 font-inter font-medium"
                                        style={{
                                            background: 'white',
                                            color: '#1f1f1f',
                                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
                                        }}
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        data-testid="google-signin-btn"
                                    >
                                        {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                                            <>
                                                <GoogleIcon />
                                                Continue with Google
                                            </>
                                        )}
                                    </motion.button>

                                    {/* Email Button */}
                                    <motion.button
                                        onClick={() => setAuthMode(isReturningUser ? 'email' : 'signup')}
                                        disabled={isLoading}
                                        className="w-full py-4 px-6 rounded-xl flex items-center justify-center gap-3 font-inter font-medium"
                                        style={{
                                            background: 'rgba(212, 175, 55, 0.1)',
                                            border: '1px solid rgba(212, 175, 55, 0.4)',
                                            color: '#D4AF37'
                                        }}
                                        whileHover={{ scale: 1.02, backgroundColor: 'rgba(212, 175, 55, 0.15)' }}
                                        whileTap={{ scale: 0.98 }}
                                        data-testid="email-signin-btn"
                                    >
                                        <Mail className="w-5 h-5" />
                                        {isReturningUser ? 'Sign in with Email' : 'Sign up with Email'}
                                    </motion.button>

                                    {/* Divider */}
                                    <div className="flex items-center gap-4 my-4">
                                        <div className="flex-1 h-px bg-primary/20" />
                                        <span className="text-primary/40 text-xs font-inter">or</span>
                                        <div className="flex-1 h-px bg-primary/20" />
                                    </div>

                                    {/* Guest Button */}
                                    <motion.button
                                        onClick={handleGuestSignIn}
                                        disabled={isLoading}
                                        className="w-full py-3 px-6 rounded-xl flex items-center justify-center gap-2 font-inter text-sm"
                                        style={{
                                            background: 'transparent',
                                            border: '1px solid rgba(212, 175, 55, 0.2)',
                                            color: 'rgba(212, 175, 55, 0.7)'
                                        }}
                                        whileHover={{ borderColor: 'rgba(212, 175, 55, 0.4)' }}
                                        whileTap={{ scale: 0.98 }}
                                        data-testid="guest-signin-btn"
                                    >
                                        👻 Try as Guest
                                    </motion.button>
                                    
                                    <p className="text-center text-primary/40 text-xs">
                                        No account needed • Your data saves locally
                                    </p>
                                </div>

                                {/* Switch between Sign In / Sign Up */}
                                <p className="mt-6 text-center text-primary/50 text-sm font-inter">
                                    {isReturningUser ? (
                                        <>New here? <button onClick={() => setAuthMode('signup')} className="text-primary/80 underline">Create account</button></>
                                    ) : (
                                        <>Already have an account? <button onClick={() => setAuthMode('email')} className="text-primary/80 underline">Sign in</button></>
                                    )}
                                </p>
                                
                                {/* Legal Links */}
                                <div className="mt-6 flex justify-center">
                                    <LegalLinks language={language} />
                                </div>

                                {/* Error */}
                                {error && (
                                    <motion.p className="mt-4 text-center text-red-400 text-sm" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                                        {error}
                                    </motion.p>
                                )}
                            </motion.div>
                        )}

                        {/* Email Sign In Form */}
                        {authMode === 'email' && (
                            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
                                <div className="flex justify-center mb-6">
                                    <div className="w-16 h-16 rounded-full flex items-center justify-center"
                                        style={{ background: 'rgba(212, 175, 55, 0.1)', border: '2px solid rgba(212, 175, 55, 0.3)' }}>
                                        <Mail className="w-7 h-7 text-primary" />
                                    </div>
                                </div>

                                <h3 className="font-cinzel text-xl text-center text-primary mb-6">Sign In</h3>

                                <form onSubmit={handleEmailSignIn} className="space-y-4">
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="Email"
                                        className="w-full px-4 py-4 rounded-xl font-inter"
                                        style={{
                                            background: 'rgba(212, 175, 55, 0.05)',
                                            border: '1px solid rgba(212, 175, 55, 0.2)',
                                            color: '#D4AF37'
                                        }}
                                        data-testid="email-input"
                                    />
                                    
                                    <div className="relative">
                                        <input
                                            type={showPassword ? 'text' : 'password'}
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            placeholder="Password"
                                            className="w-full px-4 py-4 pr-12 rounded-xl font-inter"
                                            style={{
                                                background: 'rgba(212, 175, 55, 0.05)',
                                                border: '1px solid rgba(212, 175, 55, 0.2)',
                                                color: '#D4AF37'
                                            }}
                                            data-testid="password-input"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-4 top-1/2 -translate-y-1/2 text-primary/50"
                                        >
                                            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                        </button>
                                    </div>

                                    <button
                                        type="button"
                                        onClick={() => setAuthMode('forgot')}
                                        className="text-sm text-primary/50 hover:text-primary/70"
                                    >
                                        Forgot password?
                                    </button>

                                    <motion.button
                                        type="submit"
                                        disabled={isLoading}
                                        className="w-full py-4 px-6 rounded-xl font-inter font-medium"
                                        style={{
                                            background: 'linear-gradient(135deg, #D4AF37 0%, #AA8C2C 100%)',
                                            color: '#0A0A0B'
                                        }}
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        data-testid="signin-submit-btn"
                                    >
                                        {isLoading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'Sign In'}
                                    </motion.button>
                                </form>

                                <p className="mt-4 text-center text-primary/50 text-sm">
                                    Don't have an account? <button onClick={() => setAuthMode('signup')} className="text-primary/80 underline">Sign up</button>
                                </p>

                                {error && (
                                    <motion.p className="mt-4 text-center text-red-400 text-sm" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                                        {error}
                                    </motion.p>
                                )}
                            </motion.div>
                        )}

                        {/* Email Sign Up Form */}
                        {authMode === 'signup' && (
                            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
                                <div className="flex justify-center mb-6">
                                    <div className="w-16 h-16 rounded-full flex items-center justify-center"
                                        style={{ background: 'rgba(212, 175, 55, 0.1)', border: '2px solid rgba(212, 175, 55, 0.3)' }}>
                                        <Mail className="w-7 h-7 text-primary" />
                                    </div>
                                </div>

                                <h3 className="font-cinzel text-xl text-center text-primary mb-6">Create Account</h3>

                                <form onSubmit={handleEmailSignUp} className="space-y-4">
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="Email"
                                        className="w-full px-4 py-4 rounded-xl font-inter"
                                        style={{
                                            background: 'rgba(212, 175, 55, 0.05)',
                                            border: '1px solid rgba(212, 175, 55, 0.2)',
                                            color: '#D4AF37'
                                        }}
                                        data-testid="signup-email-input"
                                    />
                                    
                                    <div className="relative">
                                        <input
                                            type={showPassword ? 'text' : 'password'}
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            placeholder="Password (min 6 characters)"
                                            className="w-full px-4 py-4 pr-12 rounded-xl font-inter"
                                            style={{
                                                background: 'rgba(212, 175, 55, 0.05)',
                                                border: '1px solid rgba(212, 175, 55, 0.2)',
                                                color: '#D4AF37'
                                            }}
                                            data-testid="signup-password-input"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-4 top-1/2 -translate-y-1/2 text-primary/50"
                                        >
                                            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                        </button>
                                    </div>

                                    <motion.button
                                        type="submit"
                                        disabled={isLoading}
                                        className="w-full py-4 px-6 rounded-xl font-inter font-medium"
                                        style={{
                                            background: 'linear-gradient(135deg, #D4AF37 0%, #AA8C2C 100%)',
                                            color: '#0A0A0B'
                                        }}
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        data-testid="signup-submit-btn"
                                    >
                                        {isLoading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'Create Account'}
                                    </motion.button>
                                </form>

                                <p className="mt-4 text-center text-primary/50 text-sm">
                                    Already have an account? <button onClick={() => setAuthMode('email')} className="text-primary/80 underline">Sign in</button>
                                </p>

                                {error && (
                                    <motion.p className="mt-4 text-center text-red-400 text-sm" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                                        {error}
                                    </motion.p>
                                )}
                            </motion.div>
                        )}

                        {/* Forgot Password Form */}
                        {authMode === 'forgot' && (
                            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
                                <div className="flex justify-center mb-6">
                                    <div className="w-16 h-16 rounded-full flex items-center justify-center"
                                        style={{ background: 'rgba(212, 175, 55, 0.1)', border: '2px solid rgba(212, 175, 55, 0.3)' }}>
                                        <Mail className="w-7 h-7 text-primary" />
                                    </div>
                                </div>

                                <h3 className="font-cinzel text-xl text-center text-primary mb-2">Reset Password</h3>
                                <p className="text-center text-primary/50 text-sm mb-6">
                                    Enter your email and we'll send you a reset link
                                </p>

                                {resetSent ? (
                                    <div className="text-center">
                                        <div className="text-4xl mb-4">📧</div>
                                        <p className="text-primary/80 mb-4">Check your inbox!</p>
                                        <p className="text-primary/50 text-sm">
                                            We sent a password reset link to<br />
                                            <span className="text-primary/80">{email}</span>
                                        </p>
                                        <button
                                            onClick={() => setAuthMode('email')}
                                            className="mt-6 text-primary/70 underline text-sm"
                                        >
                                            Back to sign in
                                        </button>
                                    </div>
                                ) : (
                                    <form onSubmit={handleResetPassword} className="space-y-4">
                                        <input
                                            type="email"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            placeholder="Email"
                                            className="w-full px-4 py-4 rounded-xl font-inter"
                                            style={{
                                                background: 'rgba(212, 175, 55, 0.05)',
                                                border: '1px solid rgba(212, 175, 55, 0.2)',
                                                color: '#D4AF37'
                                            }}
                                            data-testid="reset-email-input"
                                        />

                                        <motion.button
                                            type="submit"
                                            disabled={isLoading}
                                            className="w-full py-4 px-6 rounded-xl font-inter font-medium"
                                            style={{
                                                background: 'linear-gradient(135deg, #D4AF37 0%, #AA8C2C 100%)',
                                                color: '#0A0A0B'
                                            }}
                                            whileHover={{ scale: 1.02 }}
                                            whileTap={{ scale: 0.98 }}
                                            data-testid="reset-submit-btn"
                                        >
                                            {isLoading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'Send Reset Link'}
                                        </motion.button>
                                    </form>
                                )}

                                {error && (
                                    <motion.p className="mt-4 text-center text-red-400 text-sm" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                                        {error}
                                    </motion.p>
                                )}
                            </motion.div>
                        )}

                        {/* Terms */}
                        {!authMode && (
                            <p className="mt-6 text-center text-primary/40 text-xs font-inter">
                                By continuing, you agree to our Terms of Service<br />and Privacy Policy.
                            </p>
                        )}
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};

export default ClaimDestinyModal;
